/**
 * DEC-717: Client-side analytics helper.
 *
 * Single entry point for the small set of events the client owns
 * (pre-registration intent, post-registration role routing). Server-side
 * events (user_registered, etc.) are NOT routed through this — those
 * fire from the controllers that own the lifecycle action.
 *
 * Fire-and-forget: returns immediately; failures are silent. Analytics
 * must never break the user flow.
 *
 * Session id: a UUID generated once per browser session and held in
 * sessionStorage. Stitched to the eventual user_id at signup completion
 * via the `signupSessionId` field on completeRegistration.
 */

import api from './api';

const SESSION_KEY = 'deco_signup_session_id';

// Generate or read the session id. Browsers without crypto.randomUUID
// (older Safari) get a fallback hex random; the server doesn't enforce
// UUID format on session_id since it's an opaque stitching key.
const generateId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // nosemgrep: ajinabraham.njsscan.crypto.crypto_node.node_insecure_random_generator -- legacy-browser fallback for an opaque, non-security analytics session-stitching id (primary path uses crypto.randomUUID); never used as a token or secret.
  return 'sess-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
};

export const getSignupSessionId = () => {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = generateId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return generateId(); // sessionStorage disabled — return ephemeral id
  }
};

export const clearSignupSessionId = () => {
  try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
};

/**
 * DEC-1062: mint a fresh, opaque correlation id for one Texture
 * connect→discover→bind attempt (connect_session_id). Unlike
 * getSignupSessionId this is NOT persisted — it lives only in the calling
 * component's state for the duration of one connect click, and is threaded
 * through to the server (initiateHeadless request body) so server-side
 * discovery/bind events can attach the same id. Reuses the same
 * crypto.randomUUID-with-fallback as the signup session id.
 */
export const newConnectSessionId = () => generateId();

/**
 * Track a client-owned event. Allowed event names are enforced server-side
 * by an explicit allow-list in analyticsController; an event name not on
 * that list returns 400 (silently absorbed here).
 */
export const trackClientEvent = (event, properties = {}) => {
  try {
    api.post('/analytics/track', { event, properties }).catch(() => { /* swallow */ });
  } catch { /* swallow */ }
};
